/*
 * Route-driven state container for a singular Revision
 */

import { cloneElement, Component } from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';

import RevisionActions from '../../actions/RevisionActions';
import { isDependencyCacheValid } from '../../helpers/revisions';
import NavigationStore from '../../redux/navigation';
import RevisionStore from '../../stores/RevisionStore';
import NotFound from '../generic/NotFound';
import type { NavigationState } from '../../redux/navigation';
import type { RouterProps } from '../../types';

import { RevisionProvider } from './contexts/RevisionContext';
import type { ProjectDashboardProject, ProjectDashboardRevision } from './projectDashboardTypes';

// The paths below only require the revision itself and don't use the included issues, dependencies and scans
// that you get when you make the full request.
const minimalRequestOnly = (props) => {
  const minimalPaths = ['issues/licensing', 'issues/quality', 'issues/security'];
  const path = props.routes.slice(-2).map((r) => r.path).join('/');
  return minimalPaths.includes(path);
};

const pollRevision = _.debounce((props, locator) => {
  if (minimalRequestOnly(props)) {
    RevisionActions.loadMinimal(locator, true, true);
  } else {
    RevisionActions.load(locator, true, true);
  }
}, 7000);

type RevisionContainerProps = RouterProps & {
  routes?: any;
  project: ProjectDashboardProject;
  onProjectChange: () => void;
  children?: any;
};

type RevisionContainerState = {
  revisionData: ProjectDashboardRevision;
};

type DispatchProps = {
  setNavState: (newState: Partial<NavigationState>) => void;
};

class RevisionContainer extends Component<RevisionContainerProps & DispatchProps, RevisionContainerState> {
  static defaultProps = {
    project: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      revisionData: {
        data: {},
        status: {
          loading: true,
          error: null,
        },

        // calculated on save because it's expensive
        issues: {},
      },
    };
  }

  componentDidMount() {
    this.unsubscribeRevision = (RevisionStore as any).listen(this.onRepoChange);
    return this.onReload(this.props);
  }

  componentDidUpdate(prevProps: Readonly<RevisionContainerProps>) {
    if (
      this.props.params.revision_id !== prevProps.params.revision_id ||
      // Reload if you move to a tab that requires the full revision and you have not yet loaded the full revision.
      (!minimalRequestOnly(this.props) && !this.state.revisionData.status.fullRevisionLoaded &&
        this.props.location.pathname !== prevProps.location.pathname)
    ) {
      this.onReload(this.props);
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeRevision) {
      this.unsubscribeRevision();
    }
  }

  onReload = async (nextProps?: RevisionContainerProps) => {
    const props = nextProps || this.props;
    if (props.params.revision_id) {
      const loc = `${props.params.project_id}$${props.params.revision_id}`;

      if (minimalRequestOnly(props)) {
        return RevisionActions.loadMinimal(loc);
      }

      return RevisionActions.load(loc);
    }
  };

  onRepoChange = repos => {
    const nextState = repos[`${this.props.params.project_id}$${this.props.params.revision_id}`];
    if (nextState?.data?.locator) {
      this.props.setNavState({
        navProps: {
          revision: nextState.data,
        },
      });

      // Unsteady state, poll revision for updates
      if (
        !nextState.data.resolved ||
        !nextState.data.meta?.[0]?.last_scan ||
        !isDependencyCacheValid(nextState.data)
      ) {
        pollRevision(this.props, nextState.data.locator);
      }
    }
    if (nextState) {
      this.setState({ revisionData: nextState });
    }
  };

  unsubscribeRevision;

  render() {
    if (this.state.revisionData.status.error) {
      const err = this.state.revisionData.status.error.message || this.state.revisionData.status.error;
      if (typeof err === 'string' && err.toLowerCase().includes('not found')) {
        return <NotFound />;
      }
    }

    return (
      <RevisionProvider value={this.state.revisionData}>
        <div>
          {this.props.children &&
            cloneElement(this.props.children, {
              project: this.props.project,
              onProjectChange: this.props.onProjectChange,
              revision: this.state.revisionData,
            })}
        </div>
      </RevisionProvider>
    );
  }
}

const mapDispatchToProps: DispatchProps = {
  setNavState: NavigationStore.actions.setNavState,
};

export default connect(null, mapDispatchToProps)(RevisionContainer);
